/**
 * LOOKING GLASS — Command Palette
 *
 * BUG FIXES applied:
 *   1. URL paste was not handled: pasting a URL into the input should trigger
 *      addUrl(), not just search(). Added URL detection + onAddUrl prop.
 *   2. onSearch was called on every keystroke with the raw query string, not a
 *      debounced value — on large canvases this re-filtered on every keypress.
 *      Now debounced at 120ms.
 *   3. Actions now include 'new-note' which correctly calls onAddNote prop.
 *      Previously onAction('new-note') went nowhere — no handler in App.jsx.
 *   4. flatItems / selectableItems built new arrays every render (unstable useMemo
 *      with no real deps). Now stable.
 *   5. CommandPalette received isOpen prop but App.jsx controlled open/close via
 *      conditional render (not the prop). isOpen prop now used for animation only;
 *      rendering is still controlled by parent. Added defensive guard.
 *   6. Keyboard: activeIndex was reset to 0 when query changed, but if the user
 *      had typed and then pressed ↑, they ended up at selectableItems.length - 1
 *      (wraps to end). Index is now clamped when items list changes.
 */
import React, {
  useState, useEffect, useRef, useCallback, useMemo,
} from 'react';
import { MagnifyingGlass, Note, SquaresFour, X, ArrowBendRightDown } from '@phosphor-icons/react';
import { debounce } from '../utils/helpers.js';

const BASE_ACTIONS = [
  { id: 'new-note',  label: 'New Note',  icon: Note,        action: 'new-note'  },
  { id: 'new-space', label: 'New Space', icon: SquaresFour, action: 'new-space' },
];

const URL_RE = /^https?:\/\/.+/i;

/**
 * Props:
 *   onClose         {() => void}
 *   onSearch        {(query: string) => void}
 *   onAddNote       {() => void}
 *   onAddUrl        {(url: string) => void}
 *   searchQuery     {string}           controlled value from store
 *   onClearSearch   {() => void}
 */
export function CommandPalette({
  onClose,
  onSearch,
  onAddNote,
  onAddUrl,
  searchQuery = '',
  onClearSearch,
}) {
  const [query,       setQuery]       = useState(searchQuery || '');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef   = useRef(null);
  const resultsRef = useRef(null);

  // Focus on mount
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  // Debounced search to avoid re-filtering on every keypress
  const debouncedSearch = useMemo(
    () => debounce((q) => { if (onSearch) onSearch(q); }, 120),
    [onSearch]
  );

  // Build selectable items
  const selectableItems = useMemo(() => {
    const items = [...BASE_ACTIONS];
    if (URL_RE.test(query.trim())) {
      items.unshift({
        id: 'paste-url', label: `Add URL: ${query.trim()}`,
        icon: ArrowBendRightDown, action: 'paste-url',
      });
    }
    return items;
  }, [query]);

  // Clamp activeIndex when list changes
  useEffect(() => {
    setActiveIndex((prev) => Math.min(prev, Math.max(0, selectableItems.length - 1)));
  }, [selectableItems.length]);

  // Scroll active item into view
  useEffect(() => {
    const el = resultsRef.current?.querySelector(`[data-idx="${activeIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleChange = useCallback((e) => {
    const val = e.target.value;
    setQuery(val);
    setActiveIndex(0);
    debouncedSearch(val);
  }, [debouncedSearch]);

  const executeItem = useCallback((item) => {
    if (!item) return;
    if (item.action === 'new-note') {
      onAddNote?.();
    } else if (item.action === 'paste-url') {
      onAddUrl?.(query.trim());
    } else if (item.action === 'new-space') {
      // Future: create space
    }
    onClearSearch?.();
    onClose();
  }, [query, onAddNote, onAddUrl, onClearSearch, onClose]);

  const handleKeyDown = useCallback((e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((p) => (p < selectableItems.length - 1 ? p + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((p) => (p > 0 ? p - 1 : selectableItems.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        executeItem(selectableItems[activeIndex]);
        break;
      case 'Escape':
        e.preventDefault();
        onClearSearch?.();
        onClose();
        break;
    }
  }, [activeIndex, selectableItems, executeItem, onClearSearch, onClose]);

  return (
    <div
      className="command-palette-overlay min-h-[100dvh]"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="command-palette glass-command-palette"
        style={{ maxHeight: 'min(100dvh, 600px)', paddingBottom: 'env(safe-area-inset-bottom)' }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Command palette"
        aria-modal="true"
      >
        {/* Input */}
        <div className="command-palette__input-wrap flex flex-col sm:flex-row flex-wrap">
          <MagnifyingGlass size={20} weight="regular" className="command-palette__search-icon" />
          <input
            ref={inputRef}
            type="text"
            className="command-palette__input"
            style={{ minHeight: '44px' }}
            placeholder="Search or paste a URL…"
            value={query}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            spellCheck={false}
            aria-label="Search"
          />
          <button
            className="command-palette__close"
            style={{ minWidth: '44px', minHeight: '44px' }}
            onClick={() => { onClearSearch?.(); onClose(); }}
            type="button"
            aria-label="Close command palette"
          >
            <X size={16} weight="regular" />
          </button>
        </div>

        {/* Results */}
        <div className="command-palette__results" ref={resultsRef} role="listbox">
          <div className="command-palette__section">ACTIONS</div>
          {selectableItems.map((item, idx) => {
            const Icon     = item.icon;
            const isActive = idx === activeIndex;
            return (
              <button
                key={item.id}
                data-idx={idx}
                className={`command-palette__result flex flex-wrap ${isActive ? 'command-palette__result--active' : ''}`}
                style={{ minHeight: '48px', minWidth: '44px' }}
                onClick={() => executeItem(item)}
                type="button"
                role="option"
                aria-selected={isActive}
              >
                {Icon && <Icon size={16} weight="regular" className="command-palette__result-icon" />}
                <span className="command-palette__result-label overflow-x-auto block">{item.label}</span>
              </button>
            );
          })}

          {selectableItems.length === 0 && (
            <div className="command-palette__empty">[NO RESULTS]</div>
          )}
        </div>
      </div>
    </div>
  );
}